// packages/core/command-handlers/src/ws-command-handlers.ts
import {
  getAppErrorCause,
  isAppError,
  throwAppError,
} from '@bangle.io/base-utils';
import {
  EDITOR_SAVE_DRAIN_TIMEOUT_MS,
  WORKSPACE_STORAGE_TYPE,
} from '@bangle.io/constants';
import {
  exportWorkspaceAsZip,
  resolveAvailableTransferWsPath,
  resolveBulkTransferDestinations,
  resolveNextCopyBaseName,
  waitForSaveQueueToDrain,
} from '@bangle.io/service-core';
import { purgeLocalSupabaseWorkspaceCaches } from '@bangle.io/service-platform/supabase-cache';
import { deleteCloudWorkspace } from '@bangle.io/supabase';
import { toast } from '@bangle.io/ui-components';
import {
  EMPTY_DIRECTORY_MARKER_FILE_NAME,
  WsDirPath,
  WsPath,
} from '@bangle.io/ws-path';
import {
  noteSeedWithCreatedBy,
  resolveCreatedByLabel,
} from './created-by-seed';
import { c, getCtx } from './helper';

import { validateInputPath } from './utils';

function assertDirectoryWsPath(wsPath: string, errorPath = wsPath): WsDirPath {
  const dirPath = WsPath.fromString(wsPath).asDir();

  if (!dirPath || dirPath.isRoot) {
    throwAppError(
      'error::ws-path:invalid-ws-path',
      t.app.errors.wsPath.invalidDirectoryPath,
      {
        invalidPath: errorPath,
      },
    );
  }

  return dirPath;
}

function isUnderDirectory(filePath: WsPath, dirPath: WsDirPath): boolean {
  return (
    filePath.wsName === dirPath.wsName && filePath.path.startsWith(dirPath.path)
  );
}

function basename(path: string): string {
  return path.replace(/\/+$/, '').split('/').at(-1) || path;
}

/**
 * Immediate child directory names under `parentDir`, inferred from file paths
 * (workspaces are file-backed and do not store empty dirs without a marker).
 */
function collectImmediateChildDirNames(
  wsPaths: readonly { wsPath: string }[],
  parentDir: WsDirPath,
): Set<string> {
  const names = new Set<string>();
  const parentPath = parentDir.isRoot ? '' : parentDir.path;
  for (const { wsPath } of wsPaths) {
    const filePath = WsPath.fromString(wsPath).asFile();
    if (!filePath || filePath.wsName !== parentDir.wsName) {
      continue;
    }
    if (parentPath && !filePath.path.startsWith(parentPath)) {
      continue;
    }
    const relative = parentPath
      ? filePath.path.slice(parentPath.length)
      : filePath.path;
    const slashIndex = relative.indexOf('/');
    if (slashIndex > 0) {
      names.add(relative.slice(0, slashIndex));
    }
  }
  return names;
}

function normalizeDestinationDirWsPath(destDirWsPath: string): string {
  const separatorIndex = destDirWsPath.indexOf(':');
  if (separatorIndex < 0) {
    return destDirWsPath;
  }

  const wsName = destDirWsPath.slice(0, separatorIndex);
  const path = destDirWsPath.slice(separatorIndex + 1).replace(/^\/+/, '');
  return path ? WsDirPath.fromParts(wsName, path).wsPath : `${wsName}:`;
}

function rethrowRelocationError(
  error: unknown,
  newWsPath: string,
  fileName: string,
  fallbackMessage: string,
): never {
  if (isAppError(error)) {
    if (getAppErrorCause(error)?.name === 'error::file:already-existing') {
      throwAppError(
        'error::file:already-existing',
        t.app.errors.file.alreadyExistsInDest({ fileName }),
        { wsPath: newWsPath },
      );
    }
    throw error;
  }

  toast.error(fallbackMessage);
  throw error;
}

async function assertSourceSavesDrained(
  editorEngine: {
    hasPendingOrFailedSave: (wsPath?: string) => boolean;
    subscribeToSaveStatus: (
      listener: () => void,
      wsPath?: string,
    ) => () => void;
  },
  relocations: readonly { oldWsPath: string; newWsPath: string }[],
  operation: 'move' | 'rename',
): Promise<void> {
  const drainResults = await Promise.all(
    relocations.map(({ oldWsPath }) =>
      waitForSaveQueueToDrain(
        editorEngine,
        EDITOR_SAVE_DRAIN_TIMEOUT_MS,
        oldWsPath,
      ),
    ),
  );
  const blockedIndex = drainResults.findIndex((drained) => !drained);
  const blockedRelocation = relocations[blockedIndex];
  if (blockedRelocation) {
    throwAppError(
      'error::file:invalid-operation',
      t.app.errors.file.relocationBlockedByUnsavedChanges,
      { operation, ...blockedRelocation },
    );
  }
}

export const wsCommandHandlers = [
  c(
    'command::ws:new-note-from-input',
    ({ navigation }, { inputPath, promptRename }, key) => {
      const { dispatch } = getCtx(key);
      validateInputPath(inputPath);

      const { wsName } = navigation.resolveAtoms();

      if (!wsName) {
        throwAppError(
          'error::workspace:not-opened',
          t.app.errors.workspace.notOpened,
          {
            wsPath: inputPath,
          },
        );
      }

      // Add .md extension if not present
      if (!inputPath.endsWith(WsPath.DEFAULT_NOTE_EXTENSION)) {
        inputPath = inputPath + WsPath.DEFAULT_NOTE_EXTENSION;
      }
      const wsPath = WsPath.fromParts(wsName, inputPath).toString();

      dispatch('command::ws:create-note', {
        wsPath,
        navigate: true,
        promptRename: promptRename ?? true,
      });
    },
  ),

  c(
    'command::ws:create-note',
    async (
      { fileSystem, navigation },
      { wsPath, navigate, promptRename },
      key,
    ) => {
      const { dispatch } = getCtx(key);
      const parsedPath = WsPath.fromString(wsPath);
      const wsName = parsedPath.wsName;

      if (!wsName) {
        throwAppError(
          'error::workspace:not-opened',
          t.app.errors.workspace.notOpened,
          {
            wsPath,
          },
        );
      }

      const filePath = WsPath.assertFile(wsPath);
      const fileNameWithoutExt = filePath.fileNameWithoutExtension;
      const navigationVersion = navigation.captureNavigationVersion();
      const seed = noteSeedWithCreatedBy(await resolveCreatedByLabel());

      try {
        await fileSystem.createFile(
          wsPath,
          new File([seed], fileNameWithoutExt, {
            type: 'text/plain',
          }),
        );
        toast.success(
          t.app.toasts.fileCreated({ fileName: filePath.fileName }),
        );
      } catch (error) {
        toast.error(t.app.toasts.fileCreateFailed);
        throw error;
      }

      // A storage write can outlive the dialog. Do not let its completion
      // overwrite a navigation made while the write was pending.
      if (
        navigate &&
        navigation.isNavigationVersionCurrent(navigationVersion)
      ) {
        navigation.goWsPath(wsPath);
      }

      if (promptRename) {
        dispatch('command::ui:rename-note-dialog', { wsPath });
        return;
      }

      dispatch('command::ui:focus-editor', null);
    },
  ),

  c('command::ws:go-workspace', ({ navigation }, { wsName }) => {
    navigation.goWorkspace(wsName);
  }),
  c('command::ws:go-ws-path', ({ navigation }, { wsPath }) => {
    navigation.goWsFile(wsPath);
  }),

  c(
    'command::ws:delete-workspace',
    async ({ workspaceOps, navigation }, { wsName }) => {
      const wsInfo = await workspaceOps.getWorkspaceInfo(wsName);
      const isCloudWorkspace = wsInfo?.type === WORKSPACE_STORAGE_TYPE.Supabase;
      const workspaceId =
        isCloudWorkspace && typeof wsInfo?.metadata?.workspaceId === 'string'
          ? wsInfo.metadata.workspaceId
          : undefined;

      if (isCloudWorkspace) {
        // Cloud sync materializes a local row for every remote membership, so
        // a local-only delete is recreated on the next reload. The remote
        // removal must succeed first; on failure the workspace stays visible
        // instead of hiding something that still exists in the cloud.
        if (!workspaceId) {
          toast.error(t.app.toasts.workspaceDeleteFailed);
          throwAppError(
            'error::workspace:invalid-metadata',
            `Cloud workspace ${wsName} has no workspace id`,
            { wsName },
          );
        }

        try {
          await deleteCloudWorkspace(workspaceId);
        } catch (error) {
          toast.error(t.app.toasts.workspaceDeleteFailed);
          throwAppError(
            'error::workspace:cloud-delete-failed',
            `Could not delete cloud workspace ${wsName}`,
            {
              wsName,
              details: error instanceof Error ? error.message : String(error),
            },
          );
        }
      }

      await workspaceOps.deleteWorkspaceInfo(wsName);

      if (workspaceId) {
        // Best-effort: a local cache failure must not undo a deletion that
        // already succeeded remotely.
        try {
          await purgeLocalSupabaseWorkspaceCaches(workspaceId);
        } catch (error) {
          console.warn('failed to purge local cloud workspace caches', error);
        }
      }

      if (navigation.resolveAtoms().wsName === wsName) {
        navigation.goHome();
      }
    },
  ),

  c(
    'command::ws:delete-ws-path',
    async ({ fileSystem }, { wsPath }) => {
      const filePath = WsPath.assertFile(wsPath);

      try {
        await fileSystem.deleteFile(wsPath);
        toast.success(
          t.app.toasts.fileDeleted({ fileName: filePath.fileName }),
        );
      } catch (error) {
        toast.error(t.app.toasts.fileDeleteFailed);
        throw error;
      }
    },
  ),

  c(
    'command::ws:delete-ws-paths',
    async ({ fileSystem }, { wsPaths }) => {
      const uniqueWsPaths = [...new Set(wsPaths)];

      if (uniqueWsPaths.length === 0) {
        throwAppError(
          'error::file:invalid-note-path',
          t.app.errors.file.invalidNotePath,
          {
            invalidWsPath: '',
          },
        );
      }

      try {
        await fileSystem.deleteFiles(uniqueWsPaths);
        toast.success(
          t.app.toasts.filesDeleted({ count: uniqueWsPaths.length }),
        );
      } catch (error) {
        toast.error(t.app.toasts.filesDeleteFailed);
        throw error;
      }
    },
  ),

  c(
    'command::ws:rename-ws-path',
    async (
      { editorEngine, fileSystem, userActivityService },
      { wsPath, newWsPath },
    ) => {
      const oldPath = WsPath.fromString(wsPath);
      const newPath = WsPath.fromString(newWsPath);

      if (!oldPath.wsName) {
        throwAppError(
          'error::workspace:not-opened',
          t.app.errors.workspace.notOpened,
          {
            wsPath,
          },
        );
      }

      if (oldPath.wsName !== newPath.wsName) {
        throwAppError(
          'error::file:invalid-operation',
          t.app.errors.file.cannotRenameToDifferentWorkspace,
          {
            operation: 'rename',
            oldWsPath: wsPath,
            newWsPath,
          },
        );
      }

      const newFilePath = WsPath.assertFile(newWsPath);
      if (wsPath === newWsPath) {
        return;
      }

      await assertSourceSavesDrained(
        editorEngine,
        [{ oldWsPath: wsPath, newWsPath }],
        'rename',
      );

      // Keep the open note visible while the storage write is in flight.
      // WorkspaceState follows the durable rename event once it lands.
      try {
        await fileSystem.renameFile({
          oldWsPath: wsPath,
          newWsPath,
        });
      } catch (error) {
        rethrowRelocationError(
          error,
          newWsPath,
          newFilePath.fileName,
          t.app.toasts.fileRenameFailed,
        );
      }

      const starResult = await userActivityService.relocateStarredItem(
        oldPath,
        newPath,
      );
      if (starResult === 'failed') {
        toast.warning(
          t.app.toasts.fileRenameStarUpdateFailed({
            fileName: newFilePath.fileName,
          }),
        );
      } else {
        toast.success(
          t.app.toasts.fileRenamed({ fileName: newFilePath.fileName }),
        );
      }
    },
  ),

  c(
    'command::ws:move-ws-path',
    async (
      { editorEngine, fileSystem, userActivityService },
      { wsPath, destDirWsPath },
    ) => {
      const filePath = WsPath.assertFile(wsPath);
      const destDir = WsPath.fromString(
        normalizeDestinationDirWsPath(destDirWsPath),
      ).asDir();
      if (!destDir) {
        throwAppError(
          'error::workspace:not-opened',
          t.app.errors.workspace.notOpened,
          {
            wsPath,
          },
        );
      }

      const desiredWsPath = WsPath.fromParts(
        destDir.wsName,
        WsPath.pathJoin(destDir.isRoot ? '' : destDir.path, filePath.fileName),
      ).wsPath;

      if (wsPath === desiredWsPath) {
        return;
      }

      // Cross-workspace and same-workspace conflicts auto-rename instead of
      // failing, so the source is never stranded on a name clash.
      const { wsPath: newWsPath } = await resolveAvailableTransferWsPath(
        fileSystem,
        desiredWsPath,
      );

      await assertSourceSavesDrained(
        editorEngine,
        [{ oldWsPath: wsPath, newWsPath }],
        'move',
      );

      // Keep the open note visible while the storage write is in flight.
      // WorkspaceState follows the durable rename/create event once it lands.
      try {
        if (filePath.wsName === destDir.wsName) {
          await fileSystem.renameFile({
            oldWsPath: wsPath,
            newWsPath,
          });
        } else {
          await fileSystem.transferFile({
            oldWsPath: wsPath,
            newWsPath,
          });
        }
      } catch (error) {
        rethrowRelocationError(
          error,
          newWsPath,
          filePath.fileName,
          t.app.toasts.fileMoveFailed,
        );
      }

      const starResult = await userActivityService.relocateStarredItem(
        filePath,
        WsPath.fromString(newWsPath),
      );
      const finalName = WsPath.assertFile(newWsPath).fileName;
      if (starResult === 'failed') {
        toast.warning(
          t.app.toasts.fileMoveStarUpdateFailed({
            fileName: finalName,
          }),
        );
      } else {
        toast.success(t.app.toasts.fileMoved({ fileName: finalName }));
      }
    },
  ),

  c(
    'command::ws:move-ws-paths',
    async (
      { editorEngine, fileSystem, userActivityService },
      { wsPaths, destDirWsPath },
    ) => {
      const uniqueWsPaths = [...new Set(wsPaths)];
      if (uniqueWsPaths.length === 0) {
        throwAppError(
          'error::file:invalid-note-path',
          t.app.errors.file.invalidNotePath,
          { invalidWsPath: '' },
        );
      }
      const destDir = WsPath.fromString(
        normalizeDestinationDirWsPath(destDirWsPath),
      ).asDir();
      if (!destDir) {
        throwAppError(
          'error::workspace:not-opened',
          t.app.errors.workspace.notOpened,
          { wsPath: destDirWsPath },
        );
      }

      const desired = uniqueWsPaths.map((wsPath) => {
        const filePath = WsPath.assertFile(wsPath);
        return {
          oldWsPath: filePath.wsPath,
          desiredNewWsPath: WsPath.fromParts(
            destDir.wsName,
            WsPath.pathJoin(
              destDir.isRoot ? '' : destDir.path,
              filePath.fileName,
            ),
          ).wsPath,
        };
      });
      const actionable = desired.filter(
        ({ oldWsPath, desiredNewWsPath }) => oldWsPath !== desiredNewWsPath,
      );
      if (actionable.length === 0) {
        return;
      }

      const resolved = await resolveBulkTransferDestinations(
        fileSystem,
        actionable,
      );
      await assertSourceSavesDrained(editorEngine, resolved, 'move');

      const sameWs = resolved.filter(
        ({ oldWsPath, newWsPath }) =>
          WsPath.fromString(oldWsPath).wsName ===
          WsPath.fromString(newWsPath).wsName,
      );
      const crossWs = resolved.filter(
        ({ oldWsPath, newWsPath }) =>
          WsPath.fromString(oldWsPath).wsName !==
          WsPath.fromString(newWsPath).wsName,
      );
      try {
        if (sameWs.length > 0) {
          await fileSystem.renameFiles(sameWs);
        }
        if (crossWs.length > 0) {
          await fileSystem.transferFiles(crossWs);
        }
      } catch (error) {
        toast.error(t.app.toasts.filesMoveFailed);
        throw error;
      }

      const starResult = await userActivityService.relocateStarredItems(
        resolved.map(({ oldWsPath, newWsPath }) => ({
          oldItem: WsPath.fromString(oldWsPath),
          newItem: WsPath.fromString(newWsPath),
        })),
      );
      if (starResult === 'failed') {
        toast.warning(t.app.toasts.filesMoveStarUpdateFailed);
      } else {
        toast.success(t.app.toasts.filesMoved({ count: resolved.length }));
      }
    },
  ),

  c('command::ws:quick-new-note', ({ workspaceState }, { pathPrefix }, key) => {
    const { store, dispatch } = getCtx(key);
    const wsPaths = store.get(workspaceState.$noteWsPaths) || [];

    const untitledNotes = wsPaths
      .map((path) => path.fileNameWithoutExtension)
      .filter((name) => name.startsWith('untitled-'))
      .map((name) => {
        const num = Number.parseInt(name.replace('untitled-', ''), 10);
        return Number.isNaN(num) ? 0 : num;
      });

    const nextNum =
      untitledNotes.length > 0 ? Math.max(...untitledNotes) + 1 : 1;
    const newNoteName = `untitled-${nextNum}`;

    dispatch('command::ws:new-note-from-input', {
      inputPath: pathPrefix
        ? WsPath.pathJoin(pathPrefix, newNoteName)
        : newNoteName,
    });
  }),

  c('command::ws:create-directory', async ({ fileSystem }, { dirWsPath }) => {
    const dirPath = WsPath.fromString(dirWsPath).asDir();

    if (!dirPath) {
      throwAppError(
        'error::ws-path:invalid-ws-path',
        t.app.errors.wsPath.invalidDirectoryPath,
        {
          invalidPath: dirWsPath,
        },
      );
    }

    // Workspaces are file-backed, so empty folders need a marker file. Use a
    // non-note marker and do not open the editor — the user can add notes later.
    const markerPath = dirPath.createFilePath(EMPTY_DIRECTORY_MARKER_FILE_NAME);

    try {
      await fileSystem.createFile(
        markerPath.wsPath,
        new File([''], EMPTY_DIRECTORY_MARKER_FILE_NAME, {
          type: 'text/plain',
        }),
      );
      toast.success(
        t.app.toasts.folderCreated({
          folderName: dirPath.name || dirPath.path,
        }),
      );
    } catch (error) {
      toast.error(t.app.toasts.folderCreateFailed);
      throw error;
    }
  }),

  c(
    'command::ws:rename-directory',
    async (
      {
        editorEngine,
        fileSystem,
        userActivityService,
        workspaceState,
      },
      { oldDirWsPath, newDirWsPath },
      key,
    ) => {
      const { store } = getCtx(key);
      const oldDir = assertDirectoryWsPath(oldDirWsPath);
      const newDir = assertDirectoryWsPath(newDirWsPath);

      if (oldDir.wsName !== newDir.wsName) {
        throwAppError(
          'error::file:invalid-operation',
          t.app.errors.file.cannotRenameToDifferentWorkspace,
          {
            oldWsPath: oldDirWsPath,
            newWsPath: newDirWsPath,
            operation: 'rename-directory',
          },
        );
      }

      if (oldDir.path === newDir.path) {
        return;
      }

      if (newDir.path.startsWith(oldDir.path)) {
        throwAppError(
          'error::file:invalid-operation',
          t.app.errors.file.cannotMoveDuringRename,
          {
            oldWsPath: oldDirWsPath,
            newWsPath: newDirWsPath,
            operation: 'rename-directory',
          },
        );
      }

      const wsPaths = store.get(workspaceState.$wsPaths);
      const descendants = wsPaths.filter((path) =>
        isUnderDirectory(WsPath.fromString(path.wsPath), oldDir),
      );

      if (descendants.length === 0) {
        return;
      }

      const pairs = descendants.map((path) => {
        const filePath = WsPath.assertFile(path.wsPath);
        const suffix = filePath.path.slice(oldDir.path.length);
        return {
          oldWsPath: filePath.wsPath,
          newWsPath: WsPath.fromParts(
            oldDir.wsName,
            WsPath.pathJoin(newDir.path, suffix),
          ).wsPath,
        };
      });

      const oldPathSet = new Set(pairs.map((pair) => pair.oldWsPath));
      const existingPathSet = new Set(wsPaths.map((path) => path.wsPath));
      for (const pair of pairs) {
        if (
          existingPathSet.has(pair.newWsPath) &&
          !oldPathSet.has(pair.newWsPath)
        ) {
          throwAppError(
            'error::file:already-existing',
            t.app.errors.file.alreadyExistsInDest({
              fileName: WsPath.assertFile(pair.newWsPath).fileName,
            }),
            {
              wsPath: pair.newWsPath,
            },
          );
        }
      }

      await assertSourceSavesDrained(editorEngine, pairs, 'rename');

      try {
        await fileSystem.renameFiles(pairs);
      } catch (error) {
        toast.error(t.app.toasts.folderRenameFailed);
        throw error;
      }

      const starResult = await userActivityService.relocateStarredItems(
        pairs.map(({ oldWsPath, newWsPath }) => ({
          oldItem: WsPath.fromString(oldWsPath),
          newItem: WsPath.fromString(newWsPath),
        })),
      );
      const folderName = basename(newDir.path);
      const oldFolderName = basename(oldDir.path);
      const parentChanged =
        (oldDir.getParent()?.path ?? '') !== (newDir.getParent()?.path ?? '');
      if (starResult === 'failed') {
        toast.warning(
          t.app.toasts.folderRenameStarUpdateFailed({ folderName }),
        );
      } else if (parentChanged && oldFolderName === folderName) {
        toast.success(t.app.toasts.folderMoved({ folderName }));
      } else {
        toast.success(t.app.toasts.folderRenamed({ folderName }));
      }
    },
  ),

  c(
    'command::ws:delete-directory',
    async ({ fileSystem, workspaceState }, { dirWsPath }, key) => {
      const { store } = getCtx(key);
      const dirPath = assertDirectoryWsPath(dirWsPath);
      const descendants = store
        .get(workspaceState.$wsPaths)
        .filter((path) =>
          isUnderDirectory(WsPath.fromString(path.wsPath), dirPath),
        );

      if (descendants.length === 0) {
        return;
      }

      // Delete first, then leave the route stable. The editor and asset pages
      // derive existence from workspace-state atoms and render not-found states
      // when the routed file disappears.
      try {
        await fileSystem.deleteFiles(descendants.map((path) => path.wsPath));
        toast.success(
          t.app.toasts.folderDeleted({ folderName: basename(dirPath.path) }),
        );
      } catch (error) {
        toast.error(t.app.toasts.folderDeleteFailed);
        throw error;
      }
    },
  ),
  c('command::ws:go-ws-home', ({ navigation }) => {
    navigation.goWorkspace();
  }),

  c('command::ws:go-graph', ({ navigation }) => {
    navigation.goGraph();
  }),

  c('command::ws:refresh-file-tree', ({ fileSystem }) => {
    fileSystem.refreshFileTree();
  }),

  c(
    'command::ws:export-workspace',
    async ({ workspaceState, fileSystem }, { wsName }, key) => {
      const { store } = getCtx(key);
      const targetWsName =
        wsName || store.get(workspaceState.$currentWsName) || undefined;

      if (!targetWsName) {
        throwAppError(
          'error::workspace:not-opened',
          t.app.errors.workspace.notOpened,
          {},
        );
      }

      try {
        const result = await exportWorkspaceAsZip({
          wsName: targetWsName,
          fileSystem,
        });
        toast.success(
          t.app.toasts.workspaceExported({
            fileCount: result.fileCount,
            fileName: result.filename,
          }),
        );
      } catch (error) {
        toast.error(t.app.toasts.workspaceExportFailed);
        throw error;
      }
    },
  ),

  c(
    'command::ws:recover-note-snapshot',
    async ({ noteSnapshot, fileSystem, navigation }, { snapshotId }) => {
      const snapshot = await noteSnapshot.getSnapshot(snapshotId);
      if (!snapshot) {
        toast.error(t.app.toasts.snapshotNotFound);
        return;
      }

      const originalPath = WsPath.fromString(snapshot.wsPath).asFile();
      if (!originalPath) {
        toast.error(t.app.toasts.snapshotRecoverFailed);
        return;
      }

      // Recovery always lands in a fresh note beside the original — the
      // current note content is never overwritten. Strip a previous
      // '-recovered-<n>' suffix so recovering a recovered note doesn't stack.
      const base =
        originalPath.fileNameWithoutExtension.replace(/-recovered-\d+$/, '') ||
        originalPath.fileNameWithoutExtension;

      try {
        let newWsPath: string | undefined;
        for (let attempt = 1; attempt <= 100; attempt++) {
          const candidate = originalPath.replaceFileName(
            `${base}-recovered-${attempt}${WsPath.DEFAULT_NOTE_EXTENSION}`,
          ).wsPath;
          if (!(await fileSystem.exists(candidate))) {
            newWsPath = candidate;
            break;
          }
        }
        if (!newWsPath) {
          toast.error(t.app.toasts.snapshotRecoverFailed);
          return;
        }

        await fileSystem.createTextFile(newWsPath, snapshot.content);

        toast.success(
          t.app.toasts.snapshotRecovered({
            fileName: WsPath.assertFile(newWsPath).fileName,
          }),
        );
        navigation.goWsPath(newWsPath);
      } catch (error) {
        toast.error(t.app.toasts.snapshotRecoverFailed);
        throw error;
      }
    },
  ),

  c(
    'command::ws:clone-note',
    async ({ workspaceState, fileSystem, navigation }, { wsPath }, key) => {
      const { store } = getCtx(key);
      const currentWsPath = store.get(workspaceState.$currentWsPath);
      const targetWsPath = wsPath ? WsPath.assertFile(wsPath) : currentWsPath;

      if (!targetWsPath) {
        throwAppError(
          'error::workspace:not-opened',
          t.app.errors.workspace.noNoteOpenToClone,
          {},
        );
      }

      if (!targetWsPath.isNote()) {
        throwAppError(
          'error::file:invalid-note-path',
          t.app.errors.file.originalNoteNotFound,
          {
            invalidWsPath: targetWsPath.wsPath,
          },
        );
      }

      const targetParent = targetWsPath.getParent();
      const siblingNames = new Set<string>();
      for (const note of store.get(workspaceState.$noteWsPaths)) {
        const noteFile = WsPath.fromString(note.wsPath).asFile();
        if (!noteFile) {
          continue;
        }
        const noteParent = noteFile.getParent();
        if (noteParent?.path === targetParent?.path) {
          siblingNames.add(noteFile.fileNameWithoutExtension);
        }
      }

      const candidate = resolveNextCopyBaseName(
        targetWsPath.fileNameWithoutExtension,
        siblingNames,
      );
      const newFileName = candidate + WsPath.DEFAULT_NOTE_EXTENSION;
      const newWsPath = targetWsPath.replaceFileName(newFileName).wsPath;

      const originalFile = await fileSystem.readFile(targetWsPath.wsPath);
      if (!originalFile) {
        throwAppError(
          'error::file:invalid-note-path',
          t.app.errors.file.originalNoteNotFound,
          {
            invalidWsPath: targetWsPath.wsPath,
          },
        );
      }

      await fileSystem.createFile(
        newWsPath,
        new File([originalFile], newFileName, {
          type: originalFile.type || 'text/plain',
        }),
      );

      navigation.goWsPath(newWsPath);
    },
  ),

  c(
    'command::ws:clone-directory',
    async ({ workspaceState, fileSystem }, { dirWsPath }, key) => {
      const { store } = getCtx(key);
      const sourceDir = assertDirectoryWsPath(dirWsPath);
      const parentDir = sourceDir.getParent();
      if (!parentDir) {
        throwAppError(
          'error::ws-path:invalid-ws-path',
          t.app.errors.wsPath.invalidDirectoryPath,
          {
            invalidPath: dirWsPath,
          },
        );
      }

      const wsPaths = store.get(workspaceState.$wsPaths);
      const descendants = wsPaths.filter((path) =>
        isUnderDirectory(WsPath.fromString(path.wsPath), sourceDir),
      );

      if (descendants.length === 0) {
        throwAppError(
          'error::ws-path:invalid-ws-path',
          t.app.errors.wsPath.invalidDirectoryPath,
          {
            invalidPath: dirWsPath,
          },
        );
      }

      const siblingDirNames = collectImmediateChildDirNames(wsPaths, parentDir);
      const newFolderName = resolveNextCopyBaseName(
        sourceDir.name,
        siblingDirNames,
      );
      const newDir = parentDir.isRoot
        ? WsDirPath.fromParts(sourceDir.wsName, newFolderName)
        : WsDirPath.fromParts(
            sourceDir.wsName,
            WsPath.pathJoin(parentDir.path, newFolderName),
          );

      try {
        for (const path of descendants) {
          const filePath = WsPath.assertFile(path.wsPath);
          const suffix = filePath.path.slice(sourceDir.path.length);
          const newWsPath = WsPath.fromParts(
            sourceDir.wsName,
            WsPath.pathJoin(newDir.path, suffix),
          ).wsPath;
          const originalFile = await fileSystem.readFile(filePath.wsPath);
          if (!originalFile) {
            throwAppError(
              'error::file:invalid-note-path',
              t.app.errors.file.originalNoteNotFound,
              {
                invalidWsPath: filePath.wsPath,
              },
            );
          }
          await fileSystem.createFile(
            newWsPath,
            new File([originalFile], WsPath.assertFile(newWsPath).fileName, {
              type: originalFile.type || 'text/plain',
            }),
          );
        }
        toast.success(
          t.app.toasts.folderDuplicated({ folderName: newFolderName }),
        );
      } catch (error) {
        toast.error(t.app.toasts.folderDuplicateFailed);
        throw error;
      }
    },
  ),

  c(
    'command::ws:daily-note',
    async ({ workspaceState, fileSystem, navigation }, args, key) => {
      const { store, dispatch } = getCtx(key);
      const wsName = store.get(workspaceState.$currentWsName);
      const currentWsPath = store.get(workspaceState.$currentWsPath);

      if (!wsName) {
        throwAppError(
          'error::workspace:not-opened',
          t.app.errors.workspace.noWorkspaceForDailyNote,
          {},
        );
      }

      const today = args?.date ? new Date(args.date) : new Date();

      // Format date as YYYY-MMM-DD
      const year = today.getFullYear();
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const month = monthNames[today.getMonth()];
      const day = String(today.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      const fileName = `${formattedDate}-daily${WsPath.DEFAULT_NOTE_EXTENSION}`;

      const parentDir =
        currentWsPath?.getParent() || WsDirPath.fromString(`${wsName}:`);

      const dailyNotePath = parentDir.createFilePath(fileName);
      const dailyNoteWsPath = dailyNotePath.wsPath;

      const exists = await fileSystem.exists(dailyNoteWsPath);

      if (exists) {
        navigation.goWsPath(dailyNoteWsPath);
      } else {
        dispatch('command::ws:create-note', {
          wsPath: dailyNoteWsPath,
          navigate: true,
        });
      }
    },
  ),

  c(
    'command::workspace:toggle-star',
    async (
      { workspaceState, userActivityService },
      { wsPath: argWsPath },
      key,
    ) => {
      const { store } = getCtx(key);
      const currentOpenWsPath = store.get(
        workspaceState.$currentWsPath,
      )?.wsPath;
      const wsPathToToggleString = argWsPath ?? currentOpenWsPath;

      if (!wsPathToToggleString) {
        throwAppError(
          'error::workspace:no-note-opened',
          t.app.errors.workspace.noNoteOpened,
          {},
        );
        return;
      }

      await userActivityService.toggleStarItem(
        WsPath.fromString(wsPathToToggleString),
      );
    },
  ),
];
